// src/components/ProductManagement/AttributeManager.jsx

import React, { useState, useEffect } from "react";
import { categoryApi } from "../../services/productApi";
import AttributeInput from "./AttributeInput";
import Heading3 from "../Basic/Heading3";
import Button from "../Button";

const MAX_FEATURED = 6;

const AttributeManager = ({
  category,
  productData,
  setProductData,
  categoryAttributes,
  setCategoryAttributes
}) => {
  const [attributes, setAttributes] = useState([]);
  const prevCategoryRef = React.useRef(null);

  // تعداد کل مشخصه‌های شاخص
  const featuredCount =
    Object.values(productData.attributes || {}).filter((a) => a?.is_featured)
      .length +
    productData.custom_attributes.filter((a) => a?.is_featured).length;

  useEffect(() => {
  // اگر اولین بار است (load اولیه)
    if (prevCategoryRef.current === null) {
      prevCategoryRef.current = category;
      if (category) fetchCategoryAttributes();
      return;
    }

    // اگر دسته واقعا تغییر کرده
    if (prevCategoryRef.current !== category) {
      // 🔥 reset فقط در این حالت
      setProductData(prev => ({
        ...prev,
        attributes: {},
      }));

      fetchCategoryAttributes();
      prevCategoryRef.current = category;
    }

  }, [category]);


  const fetchCategoryAttributes = async () => {
    try {
      const res = await categoryApi.getAttributes(category);
      setAttributes(res.data || []);
      setCategoryAttributes(res.data || []);
    } catch (err) {
      console.error("Error fetching attributes:", err);
    }
  };

  // مشخصه‌های دسته
  const handleCategoryAttributeChange = (attributeId, newData) => {
    setProductData((prev) => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [attributeId]: {
          value: newData.value || "",
          is_featured: !!newData.is_featured,
        },
      },
    }));
  };

  // افزودن مشخصه سفارشی
  const handleCustomAttributeAdd = () => {
    setProductData((prev) => ({
      ...prev,
      custom_attributes: [
        ...prev.custom_attributes,
        {
          uuid: crypto.randomUUID(),
          title: "",
          value: "",
          unit: "",
          is_featured: false,
        },
      ],
    }));
  };

  // آپدیت مشخصه سفارشی
  const handleCustomAttributeChange = (index, newData) => {
    const updated = [...productData.custom_attributes];

    updated[index] = {
      ...updated[index],
      title: newData.title ?? updated[index].title,
      unit: newData.unit ?? updated[index].unit,
      value: newData.value ?? "",
      is_featured: !!newData.is_featured,
    };

    setProductData((prev) => ({
      ...prev,
      custom_attributes: updated,
    }));
  };

  // حذف مشخصه سفارشی
  const handleCustomAttributeRemove = (index) => {
    const updated = productData.custom_attributes.filter((_, i) => i !== index);

    setProductData((prev) => ({
      ...prev,
      custom_attributes: updated,
    }));
  };

  return (
    <div className="space-y-6">

      {/* مشخصه‌های دسته */}
      {attributes.length > 0 && (
        <section>
          <Heading3 text="ویژگی‌های دسته کالا" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {attributes.map((attrItem) => {
              const attr = attrItem.attribute;
              const stored = productData.attributes?.[attr.id] || {
                value: "",
                is_featured: false,
              };

              const canToggleFeatured =
                stored.is_featured || featuredCount < MAX_FEATURED;

              return (
                <AttributeInput
                  key={attr.id}
                  attribute={attr}
                  data={stored}
                  isCustom={false}
                  canToggleFeatured={canToggleFeatured}
                  onChange={(newData) =>
                    handleCategoryAttributeChange(attr.id, newData)
                  }
                />
              );
            })}
          </div>
        </section>
      )}

      {/* مشخصه‌های سفارشی */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <Heading3 text="ویژگی‌های سفارشی کالا" />
          <Button variant="primary" onClick={handleCustomAttributeAdd}>
            + افزودن ویژگی
          </Button>
        </div>

        {productData.custom_attributes.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {productData.custom_attributes.map((attr, index) => {
              const canToggleFeatured =
                attr.is_featured || featuredCount < MAX_FEATURED;

              return (
                <AttributeInput
                  key={attr.uuid}
                  attribute={{
                    id: attr.uuid,
                    name: attr.title || "مشخصه سفارشی",
                    type: "text",
                    unit: attr.unit,
                  }}
                  data={attr}
                  isCustom={true}
                  canToggleFeatured={canToggleFeatured}
                  onChange={(newData) =>
                    handleCustomAttributeChange(index, newData)
                  }
                  onDelete={() => handleCustomAttributeRemove(index)}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AttributeManager;
